<template>
  <v-card
    v-click-outside="handleFocusOut"
    flat
    tabindex="-1"
  >
    <div
      id="wrapper-div"
      class="button-wrapper"
    >
      <input
        id="min-input"
        ref="minValue"
        v-model="minValue"
        class="input outlined-input"
        step="any"
        :type="filter.type === 'date' ? 'date' : 'number'"
        :placeholder="minPlaceholder"
        @input="validateMin(minValue)"
        @keyup.enter="applyFilters()"
      >
      <div class="to-text">
        to
      </div>
      <input
        id="max-input"
        ref="maxValue"
        v-model="maxValue"
        class="input outlined-input"
        step="any"
        :type="filter.type === 'date' ? 'date' : 'number'"
        :placeholder="maxPlaceholder"
        @input="validateMax(maxValue)"
        @keyup.enter="applyFilters()"
      >
    </div>
    <!-- Include Blank checkbox -->
    <div>
      <label class="include-label">
        <input
          id="include-input"
          v-model="includeBlank"
          type="checkbox"
        >
        Include records with blank {{ filter.label }}
      </label>
    </div>
    <FilterSpecButtons
      @close-filter-specification="$emit('close-filter-specification')"
      @apply-filters="applyFilters"
    />
  </v-card>
</template>

<script>
import FilterSpecButtons from './FilterSpecButtons.vue';

export default {
  name: 'FilterSpecRange',
  components: {
    FilterSpecButtons,
  },
  props: {
    filter: {
      type: Object,
      required: true,
    },
    collection: {
      type: String,
      required: false,
      default: '',
    },
  },
  emits: [
    'close-filter-specification',
    'update-filters',
  ],
  data() {
    return {
      minValue: null,
      maxValue: null,
      includeBlank: false,
    };
  },
  computed: {
    minPlaceholder() {
      return this.getPlaceholder('Min');
    },
    maxPlaceholder() {
      return this.getPlaceholder('Max');
    },
  },
  mounted() {
    this.$refs.minValue.focus();
    // nullish checks not falsy to allow zero-values
    this.minValue = this.filter.minValue ?? null;
    this.maxValue = this.filter.maxValue ?? null;
    this.includeBlank = this.filter.includeBlank ?? false;
  },
  methods: {
    applyFilters() {
      const filter = {
        ...this.filter,
        minValue: this.minValue,
        maxValue: this.maxValue,
        includeBlank: this.includeBlank,
      };
      this.$emit('update-filters', filter);
      this.$emit('close-filter-specification');
    },
    validateMin(min) {
      this.minValue = this.validate(min);
    },
    validateMax(max) {
      this.maxValue = this.validate(max);
    },
    validate(input) {
      // if input is empty, return null to prevent attempts to parse empty string to number
      if (input === '') return null;
      // if significant digits does not exist in the filter, do not constrain the input. Weak check for nullish.
      if (this.filter.significantDigits == null) return input;
      // handles the bug in case of 0 significant digits
      const significantDig = (this.filter.significantDigits === 0) ? 0 : this.filter.significantDigits + 1;
      const inputString = input.toString();

      // index of decimal in the input
      const decimalIndex = inputString.indexOf('.');

      return (inputString.indexOf('.') > 0) ? inputString.substring(0, decimalIndex + significantDig) : input;
    },
    /** Handles logic when focus is lost. */
    handleFocusOut() {
      // Don't do anything if a child element is focused
      if (this.$el.matches(':focus-within')) return;
      this.$emit('close-filter-specification');
    },
    getPlaceholder(bound) {
      if (this.filter.type === 'date') return '';
      const prefix = this.filter.prefix ? `${this.filter.prefix} ` : '';
      const suffix = this.filter.suffix ?? '';
      return `${bound} ` + prefix + suffix;
    },
  },
};
</script>

<style scoped lang="scss">
.to-text {
  font-weight: bold;
  font-family: main.$fontPrimary;
  color: main.$npGray;
  font-size: 14pt;
}
.include-label {
  cursor: pointer;
  font-weight: bold;
  color: main.$npGray;
  font-size: 11pt;
}
.input {
  width: 100%;
  height: 40px;
  padding-right: 10px;
}
.button-wrapper {
  display: flex;
  gap: 1rem;
  align-items: center;
}
</style>
